import { lazy, Suspense } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import ErrorBoundary from "./components/common/ErrorBoundary";
import ConsentGate from "./components/consent/ConsentGate";
import VisitorConsent from "./components/consent/VisitorConsent";
import Pageviews from "./components/common/Pageviews";
import AuthProvider from "./auth/AuthProvider";
import { SessionContext, useAuth } from "./auth/session";
import SiteLayout from "./components/site/SiteLayout";
import { MemberStanding, VisitorStanding } from "./lib/hooks/standing";
import { appTitle, usePageTitle } from "./lib/pageTitle";
import { ROUTES } from "./lib/routes";
import App from "./App";

const EmailChoices = lazy(() => import("./pages/EmailChoices"));
const PrivacyPage = lazy(() =>
    import("./pages/Legal").then((page) => ({ default: page.PrivacyPage })),
);
const TermsPage = lazy(() => import("./pages/Legal").then((page) => ({ default: page.TermsPage })));

/**
 * Job Envoy on the web: one site for everyone, in three states read off
 * the header (design/artboards/SiteMap.dc.html). Signed out, every job
 * can be searched, read and applied to on the company's site; the places
 * an account keeps show what they are and one way in. Signed in on Free,
 * fit, Save, Tailor and the envoy; on a plan, the same with more tailors
 * and the stronger writers. The sign-in sits over every page and forces
 * nothing: a gated action opens its sheet.
 *
 * Three pages sit outside the frame: the email choices page, which an
 * email's footer opens with a signed token and which has to work from a
 * mail client, and the privacy policy and the terms.
 */
export default function Root() {
    return (
        <AuthProvider>
            <Pageviews />
            <RoutesBoundary />
        </AuthProvider>
    );
}

/** Anything a page throws caught short of a white screen. */
function RoutesBoundary() {
    const { pathname } = useLocation();
    usePageTitle(appTitle(pathname));
    return (
        <ErrorBoundary resetKey={pathname}>
            <Pages />
        </ErrorBoundary>
    );
}

function Pages() {
    return (
        <Routes>
            <Route
                path={ROUTES.EMAIL_CHOICES}
                element={
                    <Suspense fallback={null}>
                        <EmailChoices />
                    </Suspense>
                }
            />
            <Route
                path={ROUTES.PRIVACY}
                element={
                    <Suspense fallback={null}>
                        <PrivacyPage />
                    </Suspense>
                }
            />
            <Route
                path={ROUTES.TERMS}
                element={
                    <Suspense fallback={null}>
                        <TermsPage />
                    </Suspense>
                }
            />
            <Route path="*" element={<Site />} />
        </Routes>
    );
}

/**
 * The site, for a person signed in or for a visitor. While AuthKit is
 * still reading the session the frame and the board draw at once, with
 * nothing that depends on who it is: the header's right side, the
 * visitor's extras and the places an account keeps wait for the answer.
 *
 * Over both, what the person agreed to (components/consent): the terms
 * prompt for someone signed in, and the analytics question for anyone.
 * While the sign-in is still read, nothing is asked and nothing starts.
 */
function Site() {
    const { state } = useAuth();
    if (state.status === "signed-in") {
        return (
            <SessionContext.Provider value={state.session}>
                <ConsentGate>
                    <MemberStanding>
                        <SiteLayout>
                            <App />
                        </SiteLayout>
                    </MemberStanding>
                </ConsentGate>
            </SessionContext.Provider>
        );
    }
    return (
        <VisitorConsent ready={state.status === "signed-out"}>
            <VisitorStanding>
                <SiteLayout>
                    <App />
                </SiteLayout>
            </VisitorStanding>
        </VisitorConsent>
    );
}
